// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import React, { useEffect, useMemo } from 'react';
import { Outlet, useRouteError, isRouteErrorResponse, useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';

import { ENV } from '../../../ENV';

import { useAuth } from '../../../../web-utils/src/context/authContext';

import {
  wsConnect,
  wsDisconnect,
  wsRegister,
  wsDeregister,
} from '../../../../web-utils/src/store/Websocket/WebsocketActions';

import { usePreloadScenes } from '../../../hooks/Show/usePreloadScenes';

import { RootLayout } from '../../../components/UI/RootLayout/RootLayout';
import { Navigation } from '../../../components/Navigation/Navigation/Navigation';
import { Footer } from '../../../components/Navigation/Footer/Footer';

import { Button } from '../../../../web-utils/src/UI/Component/React/V1/Button/Button';

import styles from './styles.module.css';

const defaultUrl = ENV.REACT_APP_WEBSOCKET_URL;

export const Root = () => {
  const { token, accessToken } = useAuth();
  const userId = useSelector(state => state.user?.user?.user_id);
  const studioUuid = useSelector(state => state.studio?.studio?.studio_uuid);

  const websocketStatus = useSelector(state => state.webSocket.status);
  const { scenes } = useSelector(state => state.scenes);
  const PRELOAD_DELAY_MS = 20000; // Delay before starting preload to avoid flooding the network requests

  const shouldDisplayNavigation = useMemo(() => userId && accessToken, [userId, accessToken]);

  const dispatch = useDispatch();

  // Preload scenes images
  usePreloadScenes(accessToken ? scenes : [], PRELOAD_DELAY_MS);

  // WebSocket connection
  useEffect(() => {
    if (!token) {
      return;
    }
    // Initialize websocket on mount or when token changes
    dispatch(wsConnect({ token, url: defaultUrl }));

    // Cleanup only on unmount - close websocket connection completely
    return () => {
      dispatch(wsDisconnect());
    };
  }, [dispatch, token]);

  // Register studio event on websocket when studioUuid changes
  useEffect(() => {
    // Ensure we have a studioUuid and the WebSocket is connected before registering to studio
    if (!studioUuid || websocketStatus !== 'connected') {
      return;
    }
    // Register to studio
    dispatch(wsRegister({ data: { studioUUID: studioUuid } }));

    // Deregister from studio on unmount or when studioUuid changes
    return () => {
      if (studioUuid) {
        dispatch(wsDeregister({ data: { studioUUID: studioUuid } }));
      }
    };
  }, [dispatch, studioUuid, websocketStatus]);

  // Register for model changes on websocket when connection is established
  useEffect(() => {
    if (websocketStatus !== 'connected') {
      return;
    }
    // Register for model changes
    dispatch(wsRegister({ event: 'ModelDataChanged' }));
    // Register for equipment state changes in studio
    dispatch(
      wsRegister({
        event: 'EquipmentInStudioStateChanged',
        data: { studioUUID: studioUuid },
      })
    );
  }, [dispatch, websocketStatus, studioUuid]);

  return (
    <RootLayout navigation={shouldDisplayNavigation && <Navigation />} footer={<Footer />}>
      <Outlet />
    </RootLayout>
  );
};

export const Error = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <div className={styles.container}>
          <div className={styles.card}>
            <h1>404 - Page Not Found</h1>
            <p>The page you&rsquo;re looking for doesn&rsquo;t exist.</p>
            <Button onClick={() => navigate('/')}>Go to Home</Button>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <h1>
            {error.status} - {error.statusText}
          </h1>
          <p>{error.data}</p>
          <Button onClick={() => navigate('/')}>Go to Home</Button>
        </div>
      </div>
    );
  }

  // Unknown error fallback
  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1>Unexpected Error</h1>
        <p>{error.message || 'Something went wrong.'}</p>
        <Button onClick={() => navigate('/')}>Go to Home</Button>
      </div>
    </div>
  );
};
